import { defineStore } from 'pinia';
import { f7 } from 'framework7-vue';
import _ from 'lodash';
import { useKeycloakStore } from './keycloak.js';
import { useAuthStore } from './auth.js';
import api from '../api/client.js';

const basePath = 'message';

// `senderId` is the chat `createdBy` (the Keycloak sub), but it reaches the UI
// as a plain string from JSON, from the socket and from locally built messages.
// Comparing it with `===` against a raw value is what made every bubble render
// as "received", so every id goes through this first.
const normalizeId = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return '';
  return String(value).trim();
};

// Message kinds that are not chat bubbles: their `type` must survive grouping.
const SYSTEM_MESSAGE_TYPES = ['title', 'feed_card'];

export const useMessageStore = defineStore('message', {
  state: () => ({
    message: {},
    chatUser: {},
    chatUserLoaded: false,
    chatUserRequest: null,
    chatUserList: {},
    messageList: [],
    group: {},
    groupList: [],
    subGroup: {},
    subGroupList: [],
    blockedUser: {},
    blockedUserList: [],
    blockedUserMsg: {},
    groupAdmin: {},
    groupAdminList: [],
    counterPartUser: {},
    counterPartUserList: [],
    groupParticipantList: [],
    infoMessage: {},
    messagesData: [],
    groupUnReadMsg: {},
    allGroupsUnReadMsg: [],
    replyMessage: {},
    messageIsSelected: false,
    selectedMessages: [],
    msgCltConnected: false,
    messageSocketClient: {},
    typingMessage: {},
    loading: false,
    error: null,
  }),

  getters: {
    /**
     * Every id the signed-in user is known by. The Keycloak `sub` is always
     * present after login, `chatUser` arrives from `/message/chatUser` a moment
     * later, and both have to be accepted so a message is never mislabelled
     * while — or if — the chat profile request is still in flight.
     */
    currentUserIds: (state) => {
      const ids = new Set();
      const add = (value) => {
        const id = normalizeId(value);
        if (id) ids.add(id);
      };
      try {
        add(useAuthStore().profile?.sub);
      } catch {
        // The auth store can be torn down during logout; the other ids still work.
      }
      try {
        add(useKeycloakStore().subject);
      } catch {
        // Same as above.
      }
      add(state.chatUser?.userId);
      add(state.chatUser?.createdBy);
      return ids;
    },

    /** The id to stamp on messages this client sends. */
    currentUserId: (state) => {
      return (
        normalizeId(state.chatUser?.userId) ||
        normalizeId(state.chatUser?.createdBy) ||
        normalizeId(useAuthStore().profile?.sub) ||
        normalizeId(useKeycloakStore().subject)
      );
    },

    /** Display name to stamp on messages this client sends. */
    currentSenderName: (state) => {
      const authProfile = useAuthStore().profile || {};
      // Only the first name: this is what the sender label has always shown.
      const firstName = (value) => String(value || '').split(' ')[0].trim();
      return (
        firstName(state.chatUser?.name) ||
        String(state.chatUser?.username || '').trim() ||
        String(state.chatUser?.phoneNumber || '').trim() ||
        firstName(authProfile.given_name) ||
        firstName(authProfile.name) ||
        ''
      );
    },
  },

  actions: {
    async _api(method, url, { data, params, headers: extraHeaders, isFormData, timeout } = {}) {
      const config = {
        url,
        method,
        timeout: timeout || 30000,
        headers: {
          ...extraHeaders,
        },
      };
      if (data) config.data = data;
      if (params) config.params = params;
      if (isFormData) config.headers['Content-Type'] = 'multipart/form-data';
      const { data: responseData } = await api(config);
      return responseData;
    },

    /**
     * The one place that decides whether a message is mine. Every view should
     * call this (or the `isOwnMessage` action) instead of comparing
     * `message.senderId` to a single id, which silently marked every message as
     * incoming whenever `chatUser` had not loaded yet.
     */
    isOwnMessage(message) {
      if (!message) return false;
      return this.isOwnUserId(message.senderId);
    },

    /** Same test for a bare id, e.g. the user ids inside userMessageStatus. */
    isOwnUserId(userId) {
      const id = normalizeId(userId);
      if (!id) return false;
      return this.currentUserIds.has(id);
    },

    _getMessageType(senderId) {
      return this.isOwnMessage({ senderId }) ? 'sent' : 'received';
    },

    _groupMessages(list) {
      const messages = Array.isArray(list) ? list : [];
      this.messageList = messages;
      // One snapshot of the identity set for the whole pass: a chat profile that
      // arrives mid-loop must not split the history into two different results.
      const currentUserIds = this.currentUserIds;
      const formatDate = this.$filtersToDate?.msgDate;

      this.messagesData = _.chain(messages)
        .filter(Boolean)
        .orderBy(['sentAt'], ['asc'])
        .map((m) => {
          const isSystem = SYSTEM_MESSAGE_TYPES.includes(String(m.type || '').toLowerCase());
          const senderId = normalizeId(m.senderId);
          let type;
          if (isSystem) {
            type = m.type;
          } else if (senderId && currentUserIds.has(senderId)) {
            type = 'sent';
          } else if (senderId) {
            type = 'received';
          } else {
            // Nothing to compare against (system/imported rows): fall back to
            // whatever the backend already decided.
            type = String(m.type || '').toUpperCase() === 'SENT' ? 'sent' : 'received';
          }
          return {
            ...m,
            messageDate:
              typeof formatDate === 'function'
                ? formatDate(m.sentAt, 'day')
                : String(m.sentAt || '').slice(0, 10),
            type,
          };
        })
        .groupBy('messageDate')
        .value();
    },

    setInfoMsg(message) {
      if (!Array.isArray(this.infoMessage)) this.infoMessage = [];
      this.infoMessage.push(message);
    },

    setMsg(message) {
      this.message = message;
      const msgIndex = _.findIndex(
        this.messageList,
        (msg) => msg.id === message.id
      );
      if (msgIndex < 0) {
        this.messageList.push(message);
        this._groupMessages(this.messageList);
      }
    },

    setCounterPartUSerOnlineStatus(counterPartUser) {
      this.counterPartUser = counterPartUser;
    },

    setUnReadMsgCount(groupUnReadMsg) {
      const groupInUnreadInGroups = _.find(
        this.allGroupsUnReadMsg,
        (group) => group.id === groupUnReadMsg.id
      );
      if (groupInUnreadInGroups) {
        groupInUnreadInGroups.count += groupUnReadMsg.count;
      } else {
        this.allGroupsUnReadMsg.push(groupUnReadMsg);
      }
    },

    setReplyMsg(message) {
      this.replyMessage = message;
    },

    messageIsSelectedAction(messageIsSelected) {
      this.messageIsSelected = messageIsSelected;
    },

    selectMessage(payload) {
      if (payload.action === 'add') {
        this.selectedMessages.push(payload.msg);
      } else {
        const msgIndex = this.selectedMessages.findIndex(
          (x) => x.id === payload.msg.id
        );
        this.selectedMessages.splice(msgIndex, 1);
      }
    },

    clearSelectedMessages() {
      this.messageIsSelected = false;
      this.selectedMessages = [];
    },

    msgConnectSocketed(msgCltConnected) {
      this.msgCltConnected = msgCltConnected;
    },

    msgConnectSocketClient(messageSocketClient) {
      this.messageSocketClient = messageSocketClient;
    },

    setTypingMessage(payload) {
      this.typingMessage = payload;
    },

    async getChatUser() {
      try {
        this.chatUser = (await this._api('GET', `${basePath}/chatUser`)) || {};
        this.chatUserLoaded = Boolean(this.chatUser?.userId);
      } catch (err) {
        this.error = err?.response?.data || err.message;
      }
    },

    /**
     * Loads the chat profile once, no matter how many callers ask. `chatUser`
     * starts as `{}`, which is truthy, so a plain `if (!chatUser)` guard never
     * fires and the id stays undefined — the reason every message was drawn as
     * incoming on a cold deep link into a conversation.
     */
    async ensureChatUser() {
      if (this.chatUserLoaded && this.chatUser?.userId) return this.chatUser;
      if (!this.chatUserRequest) {
        this.chatUserRequest = this.getChatUser().finally(() => {
          this.chatUserRequest = null;
        });
      }
      await this.chatUserRequest;
      return this.chatUser;
    },

    async joinMessaging() {
      try {
        this.chatUser = await this._api('POST', `${basePath}/join`);
      } catch (err) {
        this.error = err?.response?.data || err.message;
      }
    },

    async createGroup(payload) {
      try {
        const data = await this._api('POST', `${basePath}/group/create`, { data: payload });
        this.group = data;
        f7.view.main.router.navigate(
          '/message/' + data.id + '/' + data.groupType,
          { reloadAll: false, reloadDetail: true }
        );
        f7.panel.close();
        return data;
      } catch (err) {
        this.error = err?.response?.data || err.message;
        return null;
      }
    },

    async createAssoTontineWalletShopDeliveryAcctGroup(payload) {
      try {
        this.group = await this._api('POST', `${basePath}${payload.url}`, { data: payload.data });
      } catch (err) {
        this.error = err?.response?.data || err.message;
      }
    },

    async getAssociatedGroup(payload) {
      try {
        const data = await this._api('GET', `${basePath}/associatedGroup/detail`, {
          params: { requestId: payload },
        });
        this.group = data;
        if (data.groupType === 'PERSONAL') {
          this.groupCounterPartUser(data.id);
        }
      } catch (err) {
        this.error = err?.response?.data || err.message;
      }
    },

    async updateGroupInfo(payload) {
      try {
        const data = await this._api('PUT', `${basePath}/group/update`, {
          params: {
            groupId: payload.groupId,
            changeType: payload.changeType,
            change: payload.change,
          },
        });
        this.group = data;
        f7.view.main.router.navigate(`/message/${data.id}/${data.groupType}`, {
          reloadDetail: true,
        });
      } catch (err) {
        this.error = err?.response?.data || err.message;
      }
    },

    async updateGroupAvatar(payload) {
      try {
        this.group = await this._api('POST', `${basePath}/group/update/avatar`, {
          data: payload.forms,
          isFormData: true,
        });
      } catch (err) {
        this.error = err?.response?.data || err.message;
      }
    },

    async sendFileMessage(payload) {
      try {
        this.group = await this._api('POST', `${basePath}/send/fileMessage`, {
          data: payload.forms,
          isFormData: true,
        });
      } catch (err) {
        this.error = err?.response?.data || err.message;
      }
    },

    async getGroupDetail(payload) {
      try {
        const data = await this._api('GET', `${basePath}/group/detail`, {
          params: { requestId: payload.requestId,groupType: payload.groupType },
        });
        this.group = data;
        console.log("groupdetail", data);
        // if (data.groupType === 'PERSONAL') {
        //   this.groupCounterPartUser(data.id);
        // }
      } catch (err) {
        this.error = err?.response?.data || err.message;
      }
    },

    updateGroupList(updatedGroupList) {
      this.groupList = _.orderBy(updatedGroupList, ['lastMessageAt'], ['asc']);
    },

    async getUserGroups() {
      try {
        const data = await this._api('GET', `${basePath}/user/groups`);
        this.groupList = _.orderBy(data, ['lastMessageAt'], ['asc']);
      } catch (err) {
        this.error = err?.response?.data || err.message;
      }
    },

    async getUserGroupMessages(payload) {
      try {
        // The bubbles are aligned by sender, so the identity has to be resolved
        // before the history is grouped — otherwise the first page is labelled
        // with whatever id happened to be loaded at that moment.
        await this.ensureChatUser();
        const data = await this._api('GET', `${basePath}/chat/messages`, {
          params: {
            requestId: payload.requestId,
            displayedMsg: payload.displayedMsg,
          },
        });
        this._groupMessages(data);
        if (payload._groupKey) this._lastMessagesKey = payload._groupKey;
      } catch (err) {
        this.error = err?.response?.data || err.message;
      }
    },

    updateGroupMessage(payload) {
      this.messageList = this.messageList || [];
      const idx = _.findIndex(this.messageList, (m) => m.id === payload.id);

      if (idx < 0) this.messageList.push(payload);
      else this.messageList.splice(idx, 1, payload);
      this._groupMessages(this.messageList);
    },

    async groupCounterPartUser(payload) {
      try {
        this.counterPartUser = await this._api('GET', `${basePath}/groupCounterPartUser`, {
          params: { requestId: payload },
        });
      } catch (err) {
        this.error = err?.response?.data || err.message;
      }
    },

    async getCounterPartUser(payload) {
      try {
        this.counterPartUser = await this._api('GET', `${basePath}/counterPartUser`, {
          params: { requestId: payload },
        });
      } catch (err) {
        this.error = err?.response?.data || err.message;
      }
    },

    async getCounterPartUserList() {
      try {
        this.counterPartUserList = await this._api('GET', `${basePath}/counterPartUserList`);
      } catch (err) {
        this.error = err?.response?.data || err.message;
      }
    },

    async getGroupParticipantList(payload) {
      try {
        this.groupParticipantList = await this._api('GET', `${basePath}/group/participantList`, {
          params: { requestId: payload },
        });
        return this.groupParticipantList;
      } catch (err) {
        this.error = err?.response?.data || err.message;
        return null;
      }
    },

    reactToMessage(payload) {
      const reactedMessage = { id: payload.message_id };
      this.updateGroupMessage(reactedMessage);
    },

    unReacToMessage(payload) {
      this.updateGroupMessage(payload.message);
    },

    async deleteMessage(payload) {
      try {
        const data = await this._api('PUT', `${basePath}/delete/message`, {
          params: {
            messageId: payload.messageId,
            forAll: payload.forAll,
          },
        });
        this._groupMessages(data);
      } catch (err) {
        this.error = err?.response?.data || err.message;
      }
    },

    async inviteSingleUser() {
      try {
        this.group = await this._api('POST', `${basePath}/invite`, {
          params: { userId: useKeycloakStore().subject },
        });
      } catch (err) {
        this.error = err?.response?.data || err.message;
      }
    },

    async inviteAcceptSingleUser(payload) {
      try {
        this.group = await this._api('POST', `${basePath}/invite/response`, {
          params: {
            groupId: payload.groupId,
            activate: payload.activate,
          },
        });
      } catch (err) {
        this.error = err?.response?.data || err.message;
      }
    },

    async addGroupParticipant(payload) {
      try {
        this.group = await this._api('POST', `${basePath}/group/add/participant`, {
          data: payload.chatUsers,
          params: { groupId: payload.groupId },
        });
      } catch (err) {
        this.error = err?.response?.data || err.message;
      }
    },

    async removeGroupParticipant(payload) {
      try {
        this.group = await this._api('PUT', `${basePath}/group/remove/participant`, {
          params: {
            groupId: payload.groupId,
            userId: payload.userId,
          },
        });
      } catch (err) {
        this.error = err?.response?.data || err.message;
      }
    },

    async deleteGroup(payload) {
      try {
        const data = await this._api('PUT', `${basePath}/group/delete`, {
          params: { groupId: payload.groupId },
        });
        this.groupList = _.orderBy(data, ['lastMessageAt'], ['asc']);
      } catch (err) {
        this.error = err?.response?.data || err.message;
      }
    },

    async leaveGroup(payload) {
      try {
        const data = await this._api('PUT', `${basePath}/group/leave`, {
          params: { groupId: payload.groupId },
        });
        this.groupList = _.orderBy(data, ['lastMessageAt'], ['asc']);
      } catch (err) {
        this.error = err?.response?.data || err.message;
      }
    },

    async blockUser(payload) {
      try {
        const data = await this._api('PUT', `${basePath}/user/block`, {
          params: { chatUserId: payload.chatUserId },
        });
        this.blockedUserMsg = data;
      } catch (err) {
        this.error = err?.response?.data || err.message;
        this.blockedUserMsg = err?.response?.data;
      }
    },

    async unBlockUser(payload) {
      try {
        const data = await this._api('PUT', `${basePath}/user/unblock`, {
          params: { chatUserId: payload.chatUserId },
        });
        this.blockedUserMsg = data;
      } catch (err) {
        this.error = err?.response?.data || err.message;
        this.blockedUserMsg = err?.response?.data;
      }
    },

    async grantGroupAdminRights(payload) {
      try {
        this.group = await this._api('POST', `${basePath}/group/add/adminRole`, {
          params: {
            groupId: payload.groupId,
            userId: payload.userId,
          },
        });
      } catch (err) {
        this.error = err?.response?.data || err.message;
      }
    },

    async removeGroupAdminRights(payload) {
      try {
        this.group = await this._api('PUT', `${basePath}/group/remove/adminRole`, {
          params: {
            groupId: payload.groupId,
            userId: payload.userId,
          },
        });
      } catch (err) {
        this.error = err?.response?.data || err.message;
      }
    },

    async createSubGroup(payload) {
      try {
        const data = await this._api('POST', `${basePath}/subGroup/create`, {
          data: payload.subGroup,
          params: { groupId: payload.groupId },
        });
        this.subGroup = data;
        f7.view.main.router.navigate(
          `/message/${data.id}/${data.groupType}`,
          { reloadAll: false }
        );
        f7.panel.close();
        return data;
      } catch (err) {
        this.error = err?.response?.data || err.message;
        return null;
      }
    },

    async deleteSubGroup(payload) {
      try {
        const data = await this._api('PUT', `${basePath}/subGroup/delete`, {
          params: { subGroupId: payload.subGroupId },
        });
        this.groupList = _.orderBy(data, ['lastMessageAt'], ['asc']);
      } catch (err) {
        this.error = err?.response?.data || err.message;
      }
    },

    async sendMessageToUsers(payload) {
      try {
        const data = await this._api('POST', `${basePath}/send`, { data: payload });
        f7.dialog.alert('Message sent successfully ' + data);
      } catch (err) {
        this.error = err?.response?.data || err.message;
      }
    },

    /**
     * The STOMP destination a payload has to be published to.
     *
     * It has to come from the message itself. Deriving it from the conversation
     * that happens to be open (`this.group`) sent a forwarded message to the
     * wrong topic: forwarding a group message while a 1-1 chat was open went to
     * `/message.single.*`, and forwarding to a contact while a group was open
     * went to `/message.group.<userId>`, which the broker cannot even route.
     */
    _resolveMessageEndpoint(message) {
      const payloadGroupType = String(message?.groupType || '').toUpperCase();
      const isPersonal = payloadGroupType
        ? payloadGroupType === 'PERSONAL'
        : !this.group || this.group?.groupType === 'PERSONAL';
      return isPersonal
        ? `/app/message.single.${message?.chatGroup?.id}`
        : `/app/message.group.${message?.receiverId}`;
    },

    async sendChatMessage(message) {
      try {
        const authStore = useAuthStore();
        let msgType = 'text';
        let attachments = [];
        if (authStore.documentsUrl && authStore.documentsUrl.length > 0) {
          await authStore.postR2Document({
            shortId: message.chatGroup?.id,
            bucketName: import.meta.env.VITE_R2_PUBLIC_BUCKET_NAME,
          });
          attachments = authStore.documentsUrl;
          msgType = authStore.documentsUrl[0]?.fileType || 'text';
        }
        message.attachments = attachments;
        message.msgType = msgType;
        const endpoint = this._resolveMessageEndpoint(message);
        if (this.messageSocketClient?.send) {
          await this.messageSocketClient.send(endpoint, JSON.stringify(message));
        }
        await authStore.clearDocumentsUrl();
      } catch (err) {
        this.error = err?.response?.data || err.message;
      }
    },

    /**
     * Publishes an already built message. Used for forwarding, where the
     * payload already carries the attachments of the original message and must
     * never pick up the composer's pending uploads. Unlike `sendChatMessage`
     * this one reports failure to the caller instead of swallowing it.
     */
    async sendPreparedChatMessage(message) {
      const endpoint = this._resolveMessageEndpoint(message);
      if (!this.messageSocketClient?.send) {
        this.error = 'Message socket is not connected';
        throw new Error(this.error);
      }
      try {
        await this.messageSocketClient.send(endpoint, JSON.stringify(message));
        return message;
      } catch (err) {
        this.error = err?.response?.data || err.message;
        throw err;
      }
    },

    /** The chat group that holds the 1-1 conversation with `userId`, if loaded. */
    findPersonalChatGroupId(userId) {
      const id = normalizeId(userId);
      if (!id) return null;
      const found = (this.groupList || []).find(
        (grp) =>
          String(grp?.groupType || '').toUpperCase() === 'PERSONAL' &&
          (grp.groupParticipantList || []).some(
            (member) => normalizeId(member?.userId) === id
          )
      );
      return found?.id || null;
    },

    /**
     * The chat row id of a contact. A 1-1 message is stored against the
     * recipient's `chat_user.id` (the backend does `UUID.fromString(receiverId)`
     * before it looks anything up), while `userId` is the Keycloak subject — so
     * forwarding to `userId` makes the server throw and the message is lost.
     */
    findCounterPartUserId(userId) {
      const id = normalizeId(userId);
      if (!id) return null;
      const fromList = (this.counterPartUserList || []).find(
        (user) => normalizeId(user?.userId) === id
      );
      if (fromList?.id) return fromList.id;
      for (const grp of this.groupList || []) {
        const member = (grp?.groupParticipantList || []).find(
          (user) => normalizeId(user?.userId) === id
        );
        if (member?.id) return member.id;
      }
      return null;
    },

    /**
     * `/message/single.{groupId}` needs a real chat group id: a bare user id is
     * not a UUID, so the broker drops the frame and the message is never
     * delivered. A contact the user has never talked to therefore gets their
     * 1-1 conversation created (or found) before the message goes out.
     */
    async resolvePersonalChatGroup(user) {
      const userId = normalizeId(user?.userId);
      if (!userId) return null;
      const receiverId = this.findCounterPartUserId(userId) || user?.id || null;
      const existing = this.findPersonalChatGroupId(userId);
      if (existing) return { chatGroupId: existing, receiverId };

      let created = null;
      try {
        created = await this._api('POST', `${basePath}/group/create`, {
          data: {
            name: user?.name || user?.phoneNumber || userId,
            description: null,
            groupType: 'PERSONAL',
            groupParticipantList: [{ userId }],
          },
        });
      } catch (err) {
        // 409 means the 1-1 conversation is already there, it was just missing
        // from the cached group list.
        if (err?.response?.status !== 409) throw err;
      }

      if (created?.id) {
        const idx = _.findIndex(this.groupList, (grp) => grp.id === created.id);
        if (idx < 0) this.groupList.push(created);
        else this.groupList.splice(idx, 1, created);
        this.updateGroupList(this.groupList);
        return {
          chatGroupId: created.id,
          receiverId: this.findCounterPartUserId(userId) || receiverId,
        };
      }

      await this.getUserGroups();
      return {
        chatGroupId: this.findPersonalChatGroupId(userId),
        receiverId: this.findCounterPartUserId(userId) || receiverId,
      };
    },

    reactToPost(payload) {
      const reactedMessage = { id: payload.message_id };
      this.updateGroupMessage(reactedMessage);
    },

    unReactToPost(payload) {
      this.updateGroupMessage(payload.message);
    },

    async shareFeedCardToChat({ feedCard, groupId, groupType, receiverId, receiverName, chatUser }) {
      const dt = new Date().toISOString();
      const messagesToSend = {
        text: '',
        senderId: chatUser.userId,
        senderName: chatUser.name.split(' ')[0] || chatUser.username || chatUser.phoneNumber,
        sentAt: dt,
        readAt: '',
        attachment: '',
        replyingTo: '',
        receiverId,
        receiverName,
        groupType,
        type: '',
        status: 'sent',
        chatGroup: { id: groupId },
        msgType: 'feed_card',
        feedCard,
      };
      return messagesToSend;
    },

    async protectGroup(payload) {
      try {
        const data = await this._api('PUT', `${basePath}/protect`, {
          params: {
            groupId: payload.groupId,
            protectGroup: payload.protectGroup,
          },
        });
        const idx = _.findIndex(this.groupList, (g) => g.id === data.id);
        if (idx < 0) this.groupList.push(data);
        else this.groupList.splice(idx, 1, data);
        this.groupList = _.orderBy(this.groupList, ['lastMessageAt'], ['asc']);
      } catch (err) {
        this.error = err?.response?.data || err.message;
      }
    },
  },
});
